// Unica sorgente per date e ore. Distingue due nozioni che il tipo `string` da solo
// confonde, ed è la confusione da cui nascono tutti i bug di fuso:
//
//   - GIORNO CIVILE (`LocalDateString`, 'YYYY-MM-DD'): data di nascita, data visita,
//     data misurazione. Non ha un fuso: non va MAI convertito. `new Date('2026-08-14')`
//     è mezzanotte UTC, che a ovest di Greenwich cade nel giorno precedente.
//   - ISTANTE (`InstantString`, ISO 8601): created_at, last_login_at, expires_at.
//     Ha un fuso e va reso in quello di chi guarda.
//
// Le due coppie parse/format sono inverse ciascuna nel proprio dominio: `parseLocalDate` e
// `formatLocalDate` passano entrambe per i componenti locali, mai per UTC, quindi un
// round-trip picker -> stringa -> picker non sposta il giorno.

// I due tipi sono BRANDED, cioè nominalmente distinti anche se a runtime restano stringhe: il brand
// è una proprietà fantasma che esiste solo per il compilatore (nessun costo, nessun campo reale).
//
// Servono perché come semplici alias di `string` erano documentazione senza protezione, e lo scambio
// che conta è silenzioso: `formatCivilDate` applicata a un istante ne prende i primi 10 caratteri,
// quindi da '2026-08-14T23:30:00Z' mostra il 14 agosto — ma per una clinica a Tokyo quell'istante è
// il 15 alle 08:30. Nessuna eccezione, nessun '—': solo una data plausibile e sbagliata di un
// giorno. Con il brand quello scambio non compila.
//
// Lo scambio opposto (`formatInstant` su un giorno civile) era già innocuo: `parseInstant` rifiuta
// le stringhe senza ora e il risultato è un '—' visibile.
declare const localDateBrand: unique symbol
declare const instantBrand: unique symbol

/** Giorno civile 'YYYY-MM-DD', senza fuso. */
export type LocalDateString = string & { readonly [localDateBrand]: true }

/** Istante ISO 8601 con offset (o `Z`). */
export type InstantString = string & { readonly [instantBrand]: true }

/**
 * Marca come giorno civile una stringa che arriva da fuori (payload API, storage locale, letterale
 * nei test). Restituisce `null` se non è un 'YYYY-MM-DD' valido, così il brand non può essere
 * appiccicato a un valore che non lo merita: è l'unico punto in cui la promessa viene verificata.
 */
export function asLocalDate(value: string | null | undefined): LocalDateString | null {
  return parseLocalDate(value) === null ? null : (value as LocalDateString)
}

/**
 * Marca come istante una stringa che arriva da fuori. `null` se non è interpretabile come istante
 * (una data senza ora non lo è: quella è un giorno civile).
 */
export function asInstant(value: string | null | undefined): InstantString | null {
  return parseInstant(value) === null ? null : (value as InstantString)
}

/**
 * Estende le opzioni di `Intl.DateTimeFormat` (da cui arriva anche `timeZone`) aggiungendo il
 * locale. Così un chiamante che vuole solo ore e minuti passa `{ hour: '2-digit', minute: '2-digit' }`
 * senza perdere il fuso e il locale risolti: era il motivo per cui alcune viste formattavano a mano
 * con un locale fisso invece di usare queste funzioni.
 */
export interface DateFormatOptions extends Intl.DateTimeFormatOptions {
  locale?: string
}

type Resolver = () => string | undefined

let resolveLocale: Resolver = () => undefined
let resolveTimeZone: Resolver = () => undefined

// Il locale di default è quello del runtime (browser). `main.ts` lo aggancia alla lingua
// scelta in app; senza aggancio un utente giapponese vede comunque il formato del suo
// sistema, non un formato italiano fisso.
export function configureDateLocale(resolver: Resolver): void {
  resolveLocale = resolver
}

// Fuso in cui rendere gli ISTANTI. `undefined` = fuso del browser. Va agganciato al fuso
// del tenant quando la colonna esisterà: un record di cartella clinica non deve cambiare
// orario apparente perché il medico è in viaggio.
export function configureDateTimeZone(resolver: Resolver): void {
  resolveTimeZone = resolver
}

const LOCAL_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/
const HAS_OFFSET_RE = /([Zz]|[+-]\d{2}:?\d{2})$/

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

function localeOf(options?: DateFormatOptions): string | undefined {
  return options?.locale ?? resolveLocale()
}

function timeZoneOf(options?: DateFormatOptions): string | undefined {
  return options?.timeZone ?? resolveTimeZone()
}

/** Opzioni Intl per un ISTANTE: il fuso esplicito vince, altrimenti quello risolto. */
function instantIntlOptions(options?: DateFormatOptions): Intl.DateTimeFormatOptions {
  const { locale: _locale, ...intl } = options ?? {}
  return { ...intl, timeZone: timeZoneOf(options) }
}

/**
 * Opzioni Intl per un GIORNO CIVILE: `timeZone` viene scartato deliberatamente. Applicarne uno
 * sposterebbe la mezzanotte locale in un altro giorno, che è il bug che questo modulo esiste per
 * impedire.
 */
function civilIntlOptions(options?: DateFormatOptions): Intl.DateTimeFormatOptions {
  const { locale: _locale, timeZone: _timeZone, ...intl } = options ?? {}
  return intl
}

// ---------------------------------------------------------------------------
// Giorni civili
// ---------------------------------------------------------------------------

// 'YYYY-MM-DD' -> mezzanotte LOCALE. Tollera un ISO completo usandone la sola parte data.
// Il parser accetta una stringa qualunque: è il punto di ingresso di dati non fidati, ed è dove il
// brand nasce (via asLocalDate). Sono i formatter a pretenderlo.
export function parseLocalDate(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const match = LOCAL_DATE_RE.exec(iso.trim())
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(year, month - 1, day)
  // Scarta le date inesistenti (es. '2026-02-31', che JS normalizzerebbe al 3 marzo).
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null
  }
  return date
}

// Date -> 'YYYY-MM-DD' dai componenti LOCALI. Non usa toISOString(): convertirebbe a UTC e
// sposterebbe il giorno per ogni utente con offset negativo.
export function formatLocalDate(date: Date | null | undefined): LocalDateString | null {
  if (!date || Number.isNaN(date.getTime())) return null
  // Cast lecito: la stringa è costruita qui nel formato del brand, non arriva da fuori.
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` as LocalDateString
}

export function todayLocalDate(): LocalDateString {
  return formatLocalDate(new Date()) as LocalDateString
}

// Reso di un giorno civile. Nessun `timeZone`: applicarne uno sposterebbe la mezzanotte
// locale in un altro giorno, che è esattamente il bug da evitare.
export function formatCivilDate(
  iso: LocalDateString | null | undefined,
  options?: DateFormatOptions,
): string {
  const date = parseLocalDate(iso)
  if (!date) return '—'
  return date.toLocaleDateString(localeOf(options), civilIntlOptions(options))
}

// Età in anni compiuti da un giorno civile, confrontata col giorno civile locale di oggi.
export function ageFromLocalDate(iso: LocalDateString | null | undefined): number | null {
  const birth = parseLocalDate(iso)
  if (!birth) return null
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const beforeBirthday =
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  if (beforeBirthday) age -= 1
  return age < 0 ? null : age
}

// ---------------------------------------------------------------------------
// Istanti
// ---------------------------------------------------------------------------

// Parser tollerante: una data-ora priva di offset viene interpretata come UTC, che è la
// convenzione di storage dell'applicazione. Serve perché i DTO che espongono
// `LocalDateTime` emettono ISO senza `Z`, e per spec ECMAScript quella stringa andrebbe
// letta come ora LOCALE. Resta corretto quando i DTO passeranno a `Instant`: una stringa
// che ha già `Z` o un offset non viene toccata.
// Come parseLocalDate: accetta input non fidato, il brand si crea con asInstant.
export function parseInstant(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const raw = iso.trim()
  if (!raw) return null
  // Una data senza ora è un giorno civile, non un istante: va letta con parseLocalDate.
  if (!raw.includes('T') && !raw.includes(' ')) return null
  const normalized = HAS_OFFSET_RE.test(raw) ? raw : `${raw.replace(' ', 'T')}Z`
  const date = new Date(normalized)
  return Number.isNaN(date.getTime()) ? null : date
}

export function formatInstant(
  iso: InstantString | null | undefined,
  options?: DateFormatOptions,
): string {
  const date = parseInstant(iso)
  if (!date) return '—'
  return date.toLocaleString(localeOf(options), instantIntlOptions(options))
}

/**
 * Solo la data di un istante.
 *
 * `options` NON accetta `timeStyle`, e non e' pedanteria: sotto c'e'
 * `toLocaleDateString`, che con `timeStyle` non ignora l'opzione — LANCIA
 * `TypeError: Invalid option : timeStyle`. Dentro il body di una colonna PrimeVue
 * quell'eccezione non si vede come un errore: interrompe il render dell'intera
 * DataTable, e la pagina resta indietro di un aggiornamento senza dire perche'.
 *
 * E' successo davvero, in AppUsersView, e si e' visto solo il giorno in cui un
 * utente ha avuto un `last_login_at` non nullo: con tutti i valori nulli la
 * funzione non arrivava mai a formattare niente. Chi vuole data E ora usa
 * {@link formatInstant}.
 */
export function formatInstantDate(
  iso: InstantString | null | undefined,
  options?: Omit<DateFormatOptions, 'timeStyle'>,
): string {
  const date = parseInstant(iso)
  if (!date) return '—'
  return date.toLocaleDateString(localeOf(options), instantIntlOptions(options))
}

/** Solo l'ora di un istante. `dateStyle` e' escluso per la ragione simmetrica di sopra. */
export function formatInstantTime(
  iso: InstantString | null | undefined,
  options?: Omit<DateFormatOptions, 'dateStyle'>,
): string {
  const date = parseInstant(iso)
  if (!date) return '—'
  return date.toLocaleTimeString(localeOf(options), instantIntlOptions(options))
}

// Serializza un istante verso l'API: sempre con offset, mai wall-clock nudo.
export function toInstantString(date: Date | null | undefined): InstantString | null {
  if (!date || Number.isNaN(date.getTime())) return null
  // Cast lecito: toISOString() produce per definizione un ISO con offset.
  return date.toISOString() as InstantString
}

export function isInstantPast(
  iso: InstantString | null | undefined,
  reference: Date = new Date(),
): boolean {
  const date = parseInstant(iso)
  return date !== null && date.getTime() < reference.getTime()
}

// Millisecondi trascorsi da un istante. `null` se la stringa non è interpretabile;
// negativo solo per istanti realmente futuri, non più per effetto del fuso.
export function elapsedMsSince(
  iso: InstantString | null | undefined,
  reference: Date = new Date(),
): number | null {
  const date = parseInstant(iso)
  if (!date) return null
  return reference.getTime() - date.getTime()
}
